/** Knowledge Graph page — interactive Cytoscape.js visualization + AI insights */

import { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGraphData } from '../hooks/use-graph'
import { GraphCanvas, type GraphCanvasHandle } from '../components/graph/graph-canvas'
import { GraphToolbar, type GraphFilters } from '../components/graph/graph-toolbar'
import { GraphInsightPanel } from '../components/graph/graph-insight-panel'
// GH-258: node provenance tooltip
import { NodeTooltip } from '../components/graph/node-tooltip'
import { useAppStore } from '../stores/app-store'
import { cn } from '../lib/utils'

export function GraphPage() {
  const navigate = useNavigate()
  const isDark = useAppStore((s) => s.theme === 'dark')
  const [filters, setFilters] = useState<GraphFilters>({})
  const [selectedNodes, setSelectedNodes] = useState<string[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const { data, isLoading } = useGraphData(filters)
  const canvasRef = useRef<GraphCanvasHandle>(null)

  // GH-258: tooltip state
  const [tooltipNodeId, setTooltipNodeId] = useState<string | null>(null)
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 })

  const handleNodeSelect = useCallback((nodeId: string) => {
    setSelectedNodes((prev) => {
      // Shift-click: add to selection (max 2)
      if (prev.length === 1 && prev[0] !== nodeId) return [prev[0], nodeId]
      return [nodeId]
    })
  }, [])

  const handleNodeNavigate = useCallback((docId: string) => {
    // Navigate to document editor — find slug from graph data
    const node = data?.nodes.find((n) => n.data.id === docId)
    if (node) navigate(`/doc/${node.data.id}`)
  }, [data, navigate])

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query)
    if (!query.trim()) {
      canvasRef.current?.clearHighlight()
      return
    }
    canvasRef.current?.highlightSearch(query)
  }, [])

  const handleResetView = useCallback(() => {
    setSelectedNodes([])
    setSearchQuery('')
    setTooltipNodeId(null)
    canvasRef.current?.clearHighlight()
    canvasRef.current?.fitToView()
  }, [])

  // GH-258: show tooltip on node hover; position relative to graph container
  const handleNodeHover = useCallback((nodeId: string | null, x: number, y: number) => {
    setTooltipNodeId(nodeId)
    if (nodeId) setTooltipPos({ x, y })
  }, [])

  return (
    <div className={cn('flex h-full flex-col', isDark ? 'bg-surface-0' : 'bg-neutral-50')}>
      {/* Header */}
      <div className={cn('flex items-center gap-3 border-b px-4 py-3', isDark ? 'border-white/[0.06] bg-surface-1' : 'border-neutral-200 bg-white')}>
        <button
          onClick={() => navigate('/')}
          className={cn(isDark ? 'text-neutral-400 hover:text-neutral-200' : 'text-neutral-500 hover:text-neutral-800')}
          title="Back to editor"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <h1 className={cn('text-sm font-semibold', isDark ? 'text-neutral-200' : 'text-neutral-800')}>Knowledge Graph</h1>
        {isLoading && (
          <div className={cn('h-3.5 w-3.5 animate-spin rounded-full border border-t-brand-500', isDark ? 'border-neutral-600' : 'border-neutral-300')} />
        )}
      </div>

      {/* Toolbar with search + reset */}
      <GraphToolbar
        filters={filters}
        onChange={setFilters}
        stats={data?.stats}
        searchValue={searchQuery}
        onSearch={handleSearch}
        onResetView={handleResetView}
      />

      {/* Main content: graph canvas + insight panel — `data-tour="graph-canvas"`
          anchors the onboarding tour spotlight to the whole graph region so it
          aligns whether or not the canvas has any nodes yet. */}
      <div
        data-tour="graph-canvas"
        role="region"
        aria-label="Knowledge graph"
        className="relative flex flex-1 overflow-hidden"
      >
        {data && data.nodes.length === 0 ? (
          // Empty-state when the API returned a (zero-node) graph — pure
          // cytoscape canvas would render as black void otherwise.
          <div className={cn('relative flex flex-1 items-center justify-center', isDark ? 'bg-surface-0' : 'bg-neutral-50')}>
            <div className={cn('max-w-sm text-center px-6 py-8 rounded-2xl border', isDark ? 'border-white/[0.06] bg-surface-1/60' : 'border-neutral-200 bg-white')}>
              <div className="mx-auto mb-3 h-10 w-10 rounded-full bg-brand-500/10 flex items-center justify-center">
                <svg className="h-5 w-5 text-brand-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="5" r="2" />
                  <circle cx="5" cy="19" r="2" />
                  <circle cx="19" cy="19" r="2" />
                  <path d="M12 7v4M10.5 13L6 17M13.5 13l4.5 4" />
                </svg>
              </div>
              <h2 className={cn('text-base font-semibold', isDark ? 'text-neutral-100' : 'text-neutral-900')}>Your knowledge graph is empty</h2>
              <p className={cn('mt-1.5 text-xs leading-relaxed', isDark ? 'text-neutral-400' : 'text-neutral-600')}>
                Nodes appear after you accept ingest items and commit them to your wiki. Edges appear once Vectorize indexes the new content.
              </p>
              <button
                type="button"
                onClick={() => navigate('/ingest/upload')}
                className={cn('mt-4 inline-flex items-center gap-1 rounded-md bg-brand-500/10 px-3 py-1.5 text-xs font-medium ring-1 ring-inset ring-brand-500/30 hover:bg-brand-500/15', isDark ? 'text-brand-200' : 'text-brand-700')}
              >
                Ingest documents →
              </button>
            </div>
          </div>
        ) : data ? (
          <div className="relative flex flex-1 overflow-hidden">
            <GraphCanvas
              ref={canvasRef}
              data={data}
              onNodeSelect={handleNodeSelect}
              onNodeNavigate={handleNodeNavigate}
              onNodeHover={handleNodeHover}
            />
            {/* GH-258: provenance tooltip */}
            <NodeTooltip
              nodeId={tooltipNodeId}
              x={tooltipPos.x}
              y={tooltipPos.y}
              onClose={() => setTooltipNodeId(null)}
            />
            {/* No-edges hint — populated tenant but KG hasn't produced links yet (#189) */}
            {data.nodes.length > 0
              && (data.stats?.explicitEdges ?? 0) === 0
              && (data.stats?.implicitEdges ?? 0) === 0 && (
                <div
                  role="status"
                  className="pointer-events-none absolute left-1/2 top-4 z-10 max-w-md -translate-x-1/2 rounded-xl border border-white/[0.08] bg-surface-1/85 px-4 py-2.5 text-center text-xs text-neutral-300 shadow-lg backdrop-blur-sm"
                >
                  <span className="font-medium text-neutral-100">No relationships yet.</span>
                  <span className="ml-1.5 text-neutral-400">
                    KG edges appear after commit + similarity indexing.
                  </span>
                  {import.meta.env.DEV && (
                    <span className="ml-1.5 text-neutral-500">
                      (Vectorize bindings don&apos;t run locally — this is expected in dev.)
                    </span>
                  )}
                </div>
              )}
          </div>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            {isLoading ? (
              <div className="flex flex-col items-center gap-2">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-neutral-700 border-t-brand-500" />
                <span className="text-xs text-neutral-500">Loading graph...</span>
              </div>
            ) : (
              <span className="text-sm text-neutral-600">No documents found</span>
            )}
          </div>
        )}
        <GraphInsightPanel
          selectedNodes={selectedNodes}
          onNavigate={handleNodeNavigate}
        />
      </div>
    </div>
  )
}
